import React from "react";
import AnimationRevealPage from "helpers/AnimationRevealPage.js";
import { Container } from "components/misc/Layouts";
import tw from "twin.macro";
import styled from "styled-components";
import DownloadApp from "components/cta/DownloadApp.js";
import Footer from "components/footers/FourColumnWithInputForm.js";
import QrDeviceSrc from "images/qrdevice.png";
import DoorSrc from "images/barrera.png";
import CamaraSrc from "images/camara.png";
import TwoColumnWithImage from "components/columns/TwoColumnWithImage";
import TwoColumnWithImageLeft from "components/columns/TwoColumnWithImageLeft";
import HeaderWithNavbar from "components/columns/NavbarBackgroundAsImage.js";
import ContactUsForm from "components/forms/ContactUsForm.js";
import { SectionHeading } from "components/misc/Headings.js";
import { SectionDescription } from "components/misc/Typography.js";
import { ContentWithPaddingXl } from "components/misc/Layouts.js";

const HighlightedTextInverse = tw.span`bg-gray-100 text-primary-500 px-4 transform -skew-x-12 inline-block`;

const FeaturesGrid = tw.div`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 max-w-screen-xl mx-auto px-8 py-12`;
const FeatureHeading = tw(SectionHeading)`text-center mb-4`;
const FeatureSubtitle = tw(SectionDescription)`text-center mx-auto mb-8 max-w-2xl`;
const FeatureCard = styled.div`
  ${tw`bg-white rounded-xl shadow-md p-6 flex flex-col border-l-4 border-primary-500 hover:shadow-xl transition-all duration-300`}
`;
const FeatureCardIcon = tw.div`text-3xl mb-3`;
const FeatureCardTitle = tw.h3`text-lg font-bold text-primary-700 mb-2`;
const FeatureCardDesc = tw.p`text-gray-600 text-sm leading-relaxed`;

const ModulesSection = styled.div`${tw`py-12 bg-primary-900`}`;
const ModulesInner = tw.div`max-w-screen-xl mx-auto px-8`;
const ModulesHeading = tw.h2`text-3xl font-black text-white text-center mb-4`;
const ModulesSubtitle = tw.p`text-gray-300 text-center max-w-2xl mx-auto mb-10 text-lg`;
const RolesGrid = tw.div`grid grid-cols-1 md:grid-cols-2 gap-8`;
const RoleCard = styled.div`${tw`bg-primary-800 rounded-xl p-8`}`;
const RoleTitle = tw.h3`text-xl font-bold text-white mb-4 flex items-center gap-2`;
const RoleList = tw.ul`space-y-2`;
const RoleListItem = tw.li`text-gray-300 text-sm flex items-start gap-2`;

export default () => {
  const sendMessage = (formData) => {
    const { name, email, subject, message } = formData;
    const text = `Nombre: ${name}\nEmail: ${email}\nAsunto: ${subject}\nMensaje: ${message}`;
    const phone = "3325646932";
    const url = `https://wa.me/${phone}?text=${encodeURIComponent(text)}`;
    window.open(url, "_blank");
  };

  const appFeatures = [
    { icon: "📱", title: "Invitaciones QR Digitales", desc: "El residente genera el QR desde la app y lo comparte por WhatsApp. El visitante lo recibe, activa su pase y llega sin depender de nadie en caseta." },
    { icon: "🔐", title: "QR Dinámico — Cambia cada 30s", desc: "El código se regenera automáticamente usando TOTP. Una captura de pantalla compartida caduca en segundos. Funciona offline al llegar al fraccionamiento." },
    { icon: "🛡️", title: "Verificación OTP por WhatsApp o SMS", desc: "Al activar la invitación, el visitante recibe un código de un solo uso al número registrado. Solo quien tiene el teléfono correcto puede obtener su pase." },
    { icon: "💳", title: "Gestión de Pagos y Adeudos", desc: "Consulta tu estado de cuenta, registra pagos de cuotas de mantenimiento y recibe notificaciones de vencimientos — todo desde el portal." },
    { icon: "🏊", title: "Reservación de Amenidades", desc: "Reserva la alberca, salón de eventos, cancha de tenis o cualquier espacio compartido. El administrador aprueba o rechaza desde su panel." },
    { icon: "📢", title: "Anuncios y Comunicados", desc: "El administrador publica avisos, circulares y emergencias. Todos los residentes los reciben al instante en su portal." },
    { icon: "📂", title: "Documentación del Fraccionamiento", desc: "Reglamento, actas de asambleas, planos y documentos importantes siempre disponibles para consulta en línea." },
    { icon: "📊", title: "Reportes Financieros", desc: "Administradores: genera estados de cuenta, reportes de morosidad, cortes mensuales y proyecciones de ingresos en tiempo real." },
    { icon: "👥", title: "Gestión de Propietarios", desc: "Alta, baja y modificación de propietarios y residentes. Asignación de roles, control de accesos y historial completo por unidad." },
    { icon: "📱", title: "Historial de Visitas", desc: "Consulta quién visitó tu propiedad, en qué fecha y a qué hora. Bitácora digital con fotografías del ingreso donde aplica." }
  ];

  const adminModules = [
    "Panel de administración centralizado",
    "Gestión completa de propietarios y residentes",
    "Control total de pagos y generación de recibos",
    "Administración de amenidades y calendario de reservas",
    "Reportes financieros exportables (PDF/Excel)",
    "Gestión de anuncios y comunicados masivos",
    "Control de acceso y bitácora de visitantes",
    "Catálogos configurables del fraccionamiento",
    "Panel de contabilidad y seguimiento de adeudos",
    "Configuración de roles y permisos del sistema"
  ];

  const residentModules = [
    "Registro y gestión de visitantes con QR",
    "Consulta de adeudos y estado de cuenta",
    "Pago de cuotas de mantenimiento",
    "Reservación de espacios y amenidades",
    "Lectura de anuncios y circulares",
    "Acceso a documentos del fraccionamiento",
    "Historial de visitas a mi propiedad",
    "Activación de pases QR para proveedores",
    "Comunicación directa con administración",
    "Notificaciones de eventos y emergencias"
  ];

  return (
    <AnimationRevealPage>
      <HeaderWithNavbar />
      <Container tw="bg-gray-100 -mx-8 -mt-8 pt-8 px-8">

        {/* QR Access Control */}
        <TwoColumnWithImage
          imageSrc={QrDeviceSrc}
          heading="Control de Acceso con QR Dinámico Anti-Fraude"
          description="El corazón de Conexa Home es su sistema de invitaciones QR. El residente genera un pase desde la app, lo comparte por WhatsApp y el visitante lo activa en línea (verificando su identidad con OTP). Al llegar al fraccionamiento, el código QR en pantalla cambia cada 30 segundos — sin necesidad de señal celular. El totem lo escanea, lo valida en menos de 2 segundos y abre la barrera de forma autónoma. Sin casetas. Sin esperas. Sin papeles."
        />

        {/* Physical barriers */}
        <TwoColumnWithImageLeft
          imageSrc={DoorSrc}
          heading="Barreras Físicas con Apertura Automática"
          description="El sistema conecta directamente con la pluma vehicular o torniquete peatonal del fraccionamiento. Al validar un QR o reconocer un rostro registrado, la barrera se abre sin ninguna intervención humana. Si el QR ya fue usado, si está vencido o si no pasa los filtros anti-fraude, la barrera permanece cerrada y se registra el intento fallido con fecha, hora y totem. Todo queda en la bitácora."
        />

        {/* Camera + AI */}
        <TwoColumnWithImage
          imageSrc={CamaraSrc}
          heading="Videovigilancia con Inteligencia Artificial"
          description="La cámara HiLook/Hikvision en el totem no es solo un ojo — es el segundo nivel de seguridad. Antes de consultar al servidor, el sistema aplica filtros matemáticos al fotograma: varianza Laplaciana (detecta imágenes comprimidas por WhatsApp) y transformada de Fourier (detecta el efecto Moiré cuando se presenta una pantalla frente a la cámara). Un QR impreso, fotografiado o enviado como imagen nunca pasará estos filtros, independientemente del código que contenga."
        />
      </Container>

      {/* App feature cards */}
      <ContentWithPaddingXl>
        <FeatureHeading>Todo lo que Necesitas en un Solo Portal</FeatureHeading>
        <FeatureSubtitle>
          Conexa Home es mucho más que control de acceso. Es la plataforma completa para vivir 
          y administrar tu fraccionamiento de forma moderna, segura y transparente.
        </FeatureSubtitle>
        <FeaturesGrid>
          {appFeatures.map((f, i) => (
            <FeatureCard key={i}>
              <FeatureCardIcon>{f.icon}</FeatureCardIcon>
              <FeatureCardTitle>{f.title}</FeatureCardTitle>
              <FeatureCardDesc>{f.desc}</FeatureCardDesc>
            </FeatureCard>
          ))}
        </FeaturesGrid>
      </ContentWithPaddingXl>

      {/* Roles section */}
      <ModulesSection>
        <ModulesInner>
          <ModulesHeading>Dos Roles, Una Plataforma Completa</ModulesHeading>
          <ModulesSubtitle>
            Conexa Home tiene dos perfiles claramente diferenciados. 
            Cada uno accede solo a lo que necesita, con la interfaz adecuada para su rol.
          </ModulesSubtitle>
          <RolesGrid>
            <RoleCard>
              <RoleTitle>🏛️ Administrador del Fraccionamiento</RoleTitle>
              <RoleList>
                {adminModules.map((m, i) => (
                  <RoleListItem key={i}><span>✓</span><span>{m}</span></RoleListItem>
                ))}
              </RoleList>
            </RoleCard>
            <RoleCard>
              <RoleTitle>🏠 Residente / Propietario</RoleTitle>
              <RoleList>
                {residentModules.map((m, i) => (
                  <RoleListItem key={i}><span>✓</span><span>{m}</span></RoleListItem>
                ))}
              </RoleList>
            </RoleCard>
          </RolesGrid>
        </ModulesInner>
      </ModulesSection>

      <ContactUsForm formAction={sendMessage} />

      <DownloadApp
        text={<>Próximamente espera por nuestra versión <HighlightedTextInverse>Móvil.</HighlightedTextInverse></>}
      />
      <Footer />
    </AnimationRevealPage>
  );
};